// DISPLAY names for customers and jobs.
//
// A customer and a job each have a name that is a code ("bprsky",
// "sky-uk-be-prius") and a display name that is what a person recognises
// ("Sky Database", "Prius"). Matt's rule (11 Sep, #1347): no code appears
// anywhere a person can read it — and that includes what Douglas is TOLD
// about the page, because he reads it back aloud.
//
// One helper, so a new page cannot quietly invent its own answer. It lives
// in utils rather than under components/agent because every page needs it.

import useAccount from '../hooks/useAccount'

type CustomerLike = {
  name?: string | null
  displayName?: string | null
  sessionJobs?: Array<{ name?: string | null; displayName?: string | null }> | null
}

/** The display name for `job` under `customer`, or the code itself when the
 *  session holds no display name for it (a job the user cannot see, or a
 *  fixture). Never returns '' for a non-empty code. */
export function jobLabelFor(customers: readonly CustomerLike[] | undefined | null, customer: string, job: string): string {
  if (!job) return job
  const cust = customers?.find(c => c.name === customer)
  const found = cust?.sessionJobs?.find(j => j.name === job)
  return found?.displayName || job
}

/** A `(job) => label` for one customer, from the logged-in user's session. */
export function useJobLabel(customer: string): (job: string) => string {
  const account = useAccount()
  const customers = account.user?.session_info?.sessionCusts
  return (job: string) => jobLabelFor(customers, customer, job)
}

/** The display name for a customer, or its code when there is no better. */
export function customerLabelFor(customers: readonly CustomerLike[] | undefined | null, customer: string): string {
  if (!customer) return customer
  const cust = customers?.find(c => c.name === customer)
  return cust?.displayName || customer
}

/**
 * Both labels for the page you are on.
 *
 * `job` is a function because a page can show more than one job (the agent's
 * Jobs tab, a multi-job dashboard); `customer` is a string because a page is
 * only ever about one.
 */
export function useDisplayNames(customer: string): { customer: string; job: (job: string) => string } {
  const account = useAccount()
  const customers = account.user?.session_info?.sessionCusts
  return {
    customer: customerLabelFor(customers, customer),
    job: (job: string) => jobLabelFor(customers, customer, job),
  }
}
